import {ControlledAutocompleteSelect} from '@hm/fundamental'
import {CircularProgress, FormControl, InputLabel} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {isArray} from 'lodash'
import React, {useEffect} from 'react'
import {Controller, useFormContext} from 'react-hook-form'

import {DropdownOption} from '../../../common/DropdownOption'
import {useAdminPilotUser} from '../../../common/hooks/useAdminPilotUser'
import {useTranslation} from '../../../hooks/useTranslation'
import {DynamicSelectDataScopeInput} from '../../../modules/DataScopeConfiguration'
import {
  CHANGE_COMMON_DATASCOPE_VALUE,
  GroupedRoleReducerAction
} from '../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'
import {SelectGrouped} from '../../../pages/ManageUser/components/SelectGrouped'
import {RoleOptions} from '../RoleEditLayout'
import {checkRequired} from '../utils'

const useStyles = makeStyles((theme) => ({
  multiSelect: {
    paddingTop: theme.spacing(2.5)
  }
}))

interface DynamicSelectDataScopeProps {
  isRequired: boolean
  errorMessage: any
  configuration: DynamicSelectDataScopeInput
  dataScopeName: string
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  multiSelect: boolean
  shouldDisplayAllInDropdown?: boolean
  selectedOptions: RoleOptions
  setSelectedOptions: (options: RoleOptions) => void
  groupedRoleAssignment: GroupedRoleAssignment
  loggedInUserPermissions?: Permission[] | null
}

// eslint-disable-next-line complexity
export const DynamicSelectDataScope = ({
  isRequired,
  errorMessage,
  configuration,
  dataScopeName,
  dispatchRoleStateUpdate,
  multiSelect,
  shouldDisplayAllInDropdown = true,
  selectedOptions,
  setSelectedOptions,
  groupedRoleAssignment,
  loggedInUserPermissions
}: DynamicSelectDataScopeProps) => {
  const formMethods = useFormContext<GroupedRoleAssignment>()
  const classes = useStyles()
  const {t} = useTranslation()

  const fieldName = `commonDataScopes.${dataScopeName}`
  const formValues = formMethods.watch()

  const dependencies =
    configuration.dependsOn !== undefined
      ? configuration.dependsOn
          .map((dep) => formValues.commonDataScopes[dep])
          .filter((dependency) => dependency !== undefined)
      : []

  const dependenciesMet = dependencies.every((dep) => !!dep)

  const helperText = !dependenciesMet
    ? t('roleAssignment.selectFirst', {
        dataScope: t(
          `roleAssignment.dataScope.${
            configuration.dependsOn[dependencies.findIndex((dep) => !dep)]
          }`
        )
      })
    : errorMessage

  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)

  const {
    isFetching,
    isError,
    data: options
  } = useQuery<DropdownOption[], AxiosError>(
    [dataScopeName, ...dependencies],
    async () => {
      return configuration.fetchValues(dependencies)
    },
    {enabled: !isUserAdminPilot}
  )

  const allOption =
    shouldDisplayAllInDropdown && options
      ? [{key: ' ', label: t('roleAssignment.selectAll')}, ...options]
      : options
  // Empty string as value is luckily understood by backend and MaterialUI
  const dropdownOptions = isRequired ? options : allOption

  useEffect(() => {
    const valueToSelect = dropdownOptions?.[0]?.key
    const shouldAutoSelect =
      isRequired &&
      configuration.dependsOn.every((e: string) => !!formMethods.getValues().commonDataScopes[e]) &&
      (dropdownOptions?.length || 0) === 1 &&
      !formMethods.getValues().commonDataScopes[dataScopeName]

    if (shouldAutoSelect && valueToSelect) {
      void formMethods.setValue(fieldName as keyof GroupedRoleAssignment, valueToSelect, {
        shouldValidate: true
      })
      dispatchRoleStateUpdate({
        type: CHANGE_COMMON_DATASCOPE_VALUE,
        payload: {
          name: dataScopeName,
          value: multiSelect ? [valueToSelect].concat() : valueToSelect
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOptions, formMethods])

  if (isFetching) {
    return (
      <FormControl key={dataScopeName} margin="normal" className={classes.multiSelect} fullWidth>
        <InputLabel shrink variant="standard">
          {t(`roleAssignment.dataScope.${dataScopeName}`)}
        </InputLabel>
        <CircularProgress size={20} />
      </FormControl>
    )
  }

  if (isError) {
    return (
      <FormControl key={dataScopeName} margin="normal" className={classes.multiSelect} fullWidth>
        <InputLabel shrink variant="standard">
          {t(`roleAssignment.dataScope.${dataScopeName}`)}
        </InputLabel>
        <div>{t('roleAssignment.labelFetchError')}</div>
      </FormControl>
    )
  }

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const countryDataFilter = (countryId: string) =>
    countryId === 'MOCK'
      ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
      : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

  const formattedCountryData =
    dropdownOptions &&
    dropdownOptions
      .filter((countryId) => countryDataFilter(countryId.key))
      .map((countryId) => {
        return {
          label: countryDataFilter(countryId.key)?.name ?? '',
          value: countryDataFilter(countryId.key)?.iso2.toUpperCase() ?? ''
        }
      })
      .sort((current, next) => {
        if (current.label < next.label) {
          return -1
        }
        if (current.label > next.label) {
          return 1
        }
        return 0
      })

  const isAdminPilotSingleOption =
    isUserAdminPilot &&
    ((dataScopeName === 'countryId' && selectedOptions.countryId.isSingle) ||
      (dataScopeName === 'orgUnitId' && selectedOptions.orgUnitId.isSingle) ||
      (dataScopeName === 'businessLine' && selectedOptions.businessLine.isSingle))

  const shouldDisableCustomerAdminInputs =
    isAdminPilotSingleOption &&
    (groupedRoleAssignment.variantDataScopes.customersScopes.length > 0 ||
      (groupedRoleAssignment.variantDataScopes.payerIds &&
        groupedRoleAssignment.variantDataScopes.payerIds?.length > 0))

  // eslint-disable-next-line complexity
  const renderField = ({field}) =>
    multiSelect ? (
      <div className="max-w-[600px]">
        <ControlledAutocompleteSelect
          label={t(`roleAssignment.dataScope.${dataScopeName}`)}
          isMultiSelect={true}
          data-cy={`selectdropdown-${fieldName}`}
          {...(dataScopeName === 'countryId'
            ? {options: formattedCountryData}
            : {
                options: (selectedOptions[dataScopeName]?.dropdownOptions &&
                selectedOptions[dataScopeName].dropdownOptions.length > 0
                  ? selectedOptions[dataScopeName].dropdownOptions
                  : dropdownOptions
                )?.map((option: Record<string, unknown>) => ({
                  value: option.key,
                  label: option.label
                }))
              })}
          selectedValues={
            ((field.value as unknown as string[])?.length || 0) > 0
              ? (field.value as unknown as string[])
              : []
          }
          onChange={(selectedValue) => {
            if (isUserAdminPilot) {
              setSelectedOptions({
                ...selectedOptions,
                [dataScopeName]: {
                  isSingle: false,
                  value: selectedValue,
                  dropdownOptions: selectedOptions[dataScopeName].dropdownOptions
                }
              })
            } else {
              field.onChange(selectedValue)
            }
            dispatchRoleStateUpdate({
              type: CHANGE_COMMON_DATASCOPE_VALUE,
              payload: {
                name: dataScopeName,
                value: selectedValue.map((value) => value.toString())
              }
            })
          }}
          error={helperText}
          isTouched={true}
        />
      </div>
    ) : (
      <SelectGrouped
        {...field}
        inputLabel={t(`roleAssignment.dataScope.${dataScopeName}`)}
        disabled={
          shouldDisableCustomerAdminInputs ||
          (selectedOptions[dataScopeName] !== undefined && !selectedOptions[dataScopeName].isSingle
            ? false
            : !dependenciesMet || !dropdownOptions || !dropdownOptions.length)
        }
        error={helperText}
        data-test-id={`selectdropdown-${fieldName}`}
        emptyLabel={t('roleAssignment.defaultSelect')}
        onChange={(value: string) => {
          if (isUserAdminPilot) {
            setSelectedOptions({
              ...selectedOptions,
              [dataScopeName]: {
                isSingle: false,
                value: value,
                dropdownOptions: selectedOptions[dataScopeName].dropdownOptions
              }
            })
          } else {
            field.onChange(value)
          }
          dispatchRoleStateUpdate({
            type: CHANGE_COMMON_DATASCOPE_VALUE,
            payload: {
              name: dataScopeName,
              value: multiSelect ? (isArray(value) ? value : [value]) : value
            }
          })
        }}
        searchPlaceholder={`Select ${t(`roleAssignment.dataScope.${dataScopeName}`)}`}
        showSearch={dataScopeName === 'countryId'}
        value={
          isUserAdminPilot
            ? selectedOptions[dataScopeName]?.value || ''
            : formMethods.getValues().commonDataScopes[dataScopeName] || (multiSelect ? [] : '')
        }
        {...(dataScopeName === 'countryId'
          ? {options: formattedCountryData}
          : {
              options: (selectedOptions[dataScopeName]?.dropdownOptions &&
              selectedOptions[dataScopeName].dropdownOptions.length > 0
                ? selectedOptions[dataScopeName].dropdownOptions
                : dropdownOptions
              )?.map((option: Record<string, unknown>) => ({
                value: option.key,
                label: option.label
              }))
            })}
      />
    )

  return (
    <Controller
      name={fieldName as keyof GroupedRoleAssignment}
      control={formMethods.control}
      rules={{
        validate: checkRequired(
          isRequired,
          multiSelect,
          t('roleAssignment.dataScopeRequired', {
            dataScope: t(`roleAssignment.dataScope.${dataScopeName}`)
          })
        )
      }}
      render={renderField}
    />
  )
}
